import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Tooltip, Typography } from '@mui/material';
import { Popover, Select, Spin, notification } from 'antd';
const { Option } = Select;
import { ceil, debounce, trim } from 'lodash';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';

import AgGrid from '../../../components/ag-grid-table/index';
import Modal from '../../../components/uiComponent/modal/index';
import Pagination from '../../../components/pagination/index';

import InfoIcon from '../../../assets/icons/info-black.svg';

import {
  GetUserWorkOrders,
  AddPoToDraftWorkOrder,
  CreateWorkOrder,
} from '../../../slices/work-orders';
import { GetPoItemsForDraftWoModal } from '../../../slices/po';

import { PoDetailsWrapper } from './style';

const defaultColDef = {
  resizable: true,
  sortable: false,
  filter: false,
  suppressRowClickSelection: true,
  suppressMenu: true,
  suppressMovable: true,
};

const PAGE_SIZE = 50;

const flattenPoItems = (poItems = []) => {
  const result = [];
  // Globally-unique key per flattened row. The same SKU can be mapped to more than one
  // PO item, so flattening produces multiple rows that share the same sku `id`. AG Grid
  // would then see duplicate row-node ids and leave one of the rows blank until it's
  // re-rendered (e.g. on scroll). The running index guarantees a distinct id per row.
  let rowIndex = 0;
  for (const item of poItems) {
    const { seller_skus = [] } = item || {};
    if (!seller_skus.length) continue;
    for (const sku of seller_skus) {
      result.push({
        ...sku,
        po_row_id: item.id,
        _rowKey: `${item?.id ?? 'po'}-${sku?.id ?? sku?.sku_id ?? sku?.sku ?? 'sku'}-${rowIndex++}`,
        item_name: sku.item_name || sku.title || item?.vendoritem?.title || '---',
        sku: sku.sku || sku.seller_sku || '---',
      });
    }
  }
  return result;
};

const AddToDraftWoModal = ({
  open,
  onHide,
  poId,
  items = [],
  onSuccess,
}) => {
  const dispatch = useDispatch();

  const { userWorkOrders = [], fetchingUserWorkOrders } = useSelector(
    (state) => state.workOrders || {}
  );
  const {
    draftWoModalPoItems = [],
    fetchingDraftWoModalPoItems,
  } = useSelector((state) => state.pos || {});

  const [selectedWorkOrderId, setSelectedWorkOrderId] = useState(null);
  const [newWoName, setNewWoName] = useState('');
  const [searchText, setSearchText] = useState('');
  const [searching, setSearching] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [page, setPage] = useState({ pageSize: 25, pageNumber: 1 });

  const isAllMode = !items || items.length === 0;
  const { pageNumber, pageSize } = page;

  const flattenedSelected = useMemo(
    () => (isAllMode ? [] : flattenPoItems(items)),
    [isAllMode, items]
  );

  const selectedSkuIds = useMemo(
    () => [...new Set(flattenedSelected.map(r => r.id || r.sku_id).filter(Boolean))],
    [flattenedSelected]
  );

  // On open: reset WO selection and pagination. The parent prefetches ALL PO items
  // (exportAll) before opening; we flatten them to SKU rows and paginate client-side
  // below, so both all-mode and selected-mode share the same pagination logic.
  useEffect(() => {
    if (!open) return;
    setSelectedWorkOrderId(null);
    setNewWoName('');
    setSearchText('');
    setPage({ pageSize: 25, pageNumber: 1 });
  }, [open]);

  const debouncedFetch = useCallback(
    debounce((value) => {
      dispatch(
        GetUserWorkOrders({
          page: { pageSize: PAGE_SIZE, pageNumber: 1 },
          filters: { status: 'Draft', search: trim(value) },
        })
      ).finally(() => setSearching(false));
    }, 400),
    [dispatch]
  );

  const handleSearch = (value) => {
    setSearchText(value);
    setSearching(true);
    debouncedFetch(value);
  };

  useEffect(() => () => debouncedFetch.cancel(), [debouncedFetch]);

  const isWoLoading = searching || !!fetchingUserWorkOrders;

  const draftWorkOrders = (userWorkOrders || []).filter(
    (wo) => !wo?.status || wo.status === 'Draft'
  );

  const trimmedSearch = trim(searchText);
  const exactMatchExists = trimmedSearch
    ? draftWorkOrders.some(
        (wo) => (wo.order_name || '').toLowerCase() === trimmedSearch.toLowerCase()
      )
    : false;
  const showCreateNewOption =
    !!trimmedSearch && !isWoLoading && !exactMatchExists;

  const handleSelectNewWo = () => {
    setNewWoName(trimmedSearch);
    setSelectedWorkOrderId(null);
  };

  const clearNewWo = () => {
    setNewWoName('');
    setSearchText('');
    debouncedFetch.cancel();
    setSearching(true);
    dispatch(
      GetUserWorkOrders({
        page: { pageSize: PAGE_SIZE, pageNumber: 1 },
        filters: { status: 'Draft', search: '' },
      })
    ).finally(() => setSearching(false));
  };

  // In all-mode the table is SKU-based, so the count must reflect the flattened SKU
  // rows actually rendered — not the backend's PO-item count (a PO item can map to
  // zero or multiple SKUs, so the two diverge).
  const allModeFlattened = useMemo(
    () => flattenPoItems(draftWoModalPoItems),
    [draftWoModalPoItems]
  );

  const flattenedRows = isAllMode ? allModeFlattened : flattenedSelected;

  const previewRows = useMemo(() => {
    const start = (pageNumber - 1) * pageSize;
    return flattenedRows.slice(start, start + pageSize);
  }, [flattenedRows, pageNumber, pageSize]);

  const totalRows = flattenedRows.length;
  const tableLoading = submitting || (isAllMode && !!fetchingDraftWoModalPoItems);
  const submitSkuIds = isAllMode ? [] : selectedSkuIds;

  const InfoTooltipData = ({ products_distributions }) => {
    let dists = products_distributions;
    if (typeof dists === 'string') {
      try { dists = JSON.parse(dists); } catch (e) { dists = []; }
    }
    return (
      <PoDetailsWrapper className='mt-0 wh-stock-tooltip'>
        <div className='container-box'>
          <div className='items'>
            <span style={{ fontWeight: 'bolder' }}>Identifier / Product</span>
            <span style={{ fontWeight: 'bolder' }}>WH Stock</span>
          </div>
          {(dists || []).map(({ name, bundle_quantity, identifier_value, warehouse_stock }, idx) => (
            <div className='items' key={idx}>
              <span>{identifier_value} / {name || '---'}</span>
              <span>{warehouse_stock || 0} - (Pack Of: {bundle_quantity || 0})</span>
            </div>
          ))}
        </div>
      </PoDetailsWrapper>
    );
  };

  const columnDefs = useMemo(() => [
    {
      headerName: "Title",
      sortable: true,
      filter: true,
      minWidth: 135,
      flex: 1,
      field: "item_name",
      cellRenderer: ({ data }) => (
        <Tooltip placement="bottom" title={data?.item_name || ''}>
          <span>{data?.item_name || '---'}</span>
        </Tooltip>
      ),
    },
    { headerName: "ASIN", field: "vendorasin.asin", minWidth: 120, flex: 1 },
    { headerName: "SKU", field: "sku", minWidth: 100, flex: 1 },
    {
      headerName: "Packed Quantity", minWidth: 100, flex: 1,
      field: "po_sku_mappings_v2.bundle_quantity",
      cellRenderer: ({ value }) => value || 0,
    },
    {
      headerName: "Ship Quantity", minWidth: 100, flex: 1,
      field: "po_sku_mappings_v2.ship_quantity",
      cellRenderer: ({ value }) => value || 0,
    },
    {
      headerName: "Available Stock", minWidth: 130, flex: 1,
      field: "total_quantity",
      cellRenderer: ({ value }) => value || 0,
    },
    {
      headerName: "WH Stock", minWidth: 130, flex: 1,
      field: "warehouse_stock",
      cellRenderer: ({ data, value }) => (
        <div className='card-wrapper gap-1'>
          <span>{value || 0}</span>
          <Popover title={null} content={<InfoTooltipData products_distributions={data?.products_distributions} />} placement='right'>
            <img src={InfoIcon} className='info-icon-de' alt="InfoIcon" title="Info-Icon" />
          </Popover>
        </div>
      ),
    },
    {
      headerName: "Gated Status", minWidth: 120, flex: 1,
      field: "sku_status",
      cellRenderer: ({ value }) => (value?.toLowerCase() === 'non-gated' ? 'Not Gated' : value || '---'),
    },
  ], []);

  const handleSave = async () => {
    if (!selectedWorkOrderId && !newWoName) {
      notification.warning({ message: 'Select a draft work order or enter a new name to continue' });
      return;
    }
    setSubmitting(true);

    if (newWoName) {
      const { payload } = await dispatch(
        CreateWorkOrder({
          po_id: poId,
          order_name: newWoName,
          ...(isAllMode ? {} : { sku_ids: submitSkuIds }),
        })
      );
      setSubmitting(false);

      if (payload?.success && payload.workOrderId) {
        onSuccess?.(payload.workOrderId);
        onHide();
      }
      // Error/duplicate-name: thunk's handleRejectedResponse already surfaces a notification.
      return;
    }

    const { payload } = await dispatch(
      AddPoToDraftWorkOrder({
        workOrderId: selectedWorkOrderId,
        po_id: poId,
        sku_ids: isAllMode ? undefined : (submitSkuIds.length ? submitSkuIds : undefined),
      })
    );
    setSubmitting(false);

    if (payload?.success) {
      const addedCount = payload.added?.length || 0;
      if (addedCount === 0) {
        notification.error({
          message: 'No valid SKU(s)',
          description: 'No SKUs could be added to the work order.',
        });
        return;
      }
      notification.success({
        message: `${addedCount} SKU(s) added`,
      });
      onSuccess?.(selectedWorkOrderId);
      onHide();
    }
    // Error case: thunk's handleRejectedResponse already shows an error notification — no duplicate here
  };

  const handlePagination = (number, key) => {
    const value = Number(number);
    if (key === 'pageSize') setPage({ pageNumber: 1, pageSize: value });
    else setPage(prev => ({ ...prev, pageNumber: value }));
  };

  const refreshGrid = () => {
    if (!poId || !isAllMode) return;
    dispatch(GetPoItemsForDraftWoModal({
      id: poId,
      exportAll: true,
    }));
  };

  return (
    <Modal
      open={open}
      onClose={onHide}
      onHide={onHide}
      closeText="Cancel"
      saveText={submitting ? (newWoName ? 'Creating...' : 'Adding...') : (newWoName ? 'Create Work Order' : 'Add to Work Order')}
      removeIcon
      className="table-ui-modal-wrapper"
      closeButton={true}
      buttonDisabled={(!selectedWorkOrderId && !newWoName) || submitting}
      onSave={handleSave}
    >
      <PoDetailsWrapper>
        <Typography fontSize={24} fontWeight={600} color="#1565D8" mb={2}>
          {isAllMode ? 'Add All PO Items to Draft Work Order' : 'Add Selected Items to Draft Work Order'}
        </Typography>

        <div style={{ marginBottom: 16 }}>
          <Typography fontSize={14} fontWeight={700} color="#1B2559" mb={0.75}>
            Draft Work Order
          </Typography>
          {newWoName ? (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 12,
                padding: '10px 14px',
                border: '1px solid #1565D8',
                borderRadius: 6,
                background: '#EBF3FE',
              }}
            >
              <span style={{ fontSize: 13, color: '#1B2559' }}>
                <span style={{ fontWeight: 600 }}>New work order:</span> {newWoName}
              </span>
              <span
                onClick={clearNewWo}
                style={{ cursor: 'pointer', color: '#1565D8', fontSize: 13, fontWeight: 600 }}
              >
                Change
              </span>
            </div>
          ) : (
            <Select
              showSearch
              allowClear
              placeholder="Search existing or type a new work order name"
              value={selectedWorkOrderId || undefined}
              onChange={(val) => setSelectedWorkOrderId(val || null)}
              onSearch={handleSearch}
              filterOption={false}
              getPopupContainer={(trigger) => trigger.parentNode}
              dropdownStyle={{ zIndex: 1100 }}
              dropdownRender={(menu) => (
                <Spin spinning={isWoLoading} size="small" wrapperClassName="wo-dropdown-spin">
                  {menu}
                  {showCreateNewOption && (
                    <div
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={handleSelectNewWo}
                      style={{
                        padding: '8px 12px',
                        borderTop: '1px solid #f0f0f0',
                        cursor: 'pointer',
                        color: '#1565D8',
                        fontSize: 13,
                        fontWeight: 600,
                      }}
                    >
                      + Create new work order: "{trimmedSearch}"
                    </div>
                  )}
                </Spin>
              )}
              notFoundContent={
                isWoLoading
                  ? <div style={{ padding: 24 }} />
                  : showCreateNewOption
                    ? <div style={{ padding: 8, textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>
                        No matching draft work order
                      </div>
                    : <div style={{ padding: 8, textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>
                        No Draft Work Order Found
                      </div>
              }
              style={{ width: '100%' }}
              size="large"
            >
              {draftWorkOrders.map((wo) => (
                <Option key={wo.id} value={wo.id} label={wo.order_name || `WO #${wo.id}`}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
                    <span style={{ color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {wo.order_name || `WO #${wo.id}`}
                    </span>
                    <span style={{ fontSize: 11, color: '#6b7280', flexShrink: 0 }}>
                      {wo.created_at ? new Date(wo.created_at).toLocaleDateString() : '—'}
                    </span>
                  </div>
                </Option>
              ))}
            </Select>
          )}
        </div>

        <Typography fontSize={14} fontWeight={700} color="#1B2559" mb={0.75}>
          Items to be Added ({totalRows})
        </Typography>
        <Spin tip='Loading...' spinning={tableLoading}>
          <AgGrid
            user_referrals={previewRows}
            defaultColDef={defaultColDef}
            columnDefinitions={columnDefs}
            gridOptions={{ overlayNoRowsTemplate: '<span></span>' }}
            getRowNodeId={(data) => data._rowKey}
            rowHeight={48}
            headerHeight={36}
            containerHeight="380px"
            masterDetail={true}
            detailRowAutoHeight={true}
            className="sku-table"
            rowSelection='multiple'
          />
        </Spin>
        <Pagination
          total={totalRows}
          totalPages={ceil(totalRows / pageSize + 1)}
          pageNumber={pageNumber}
          pageLimit={pageSize}
          handleLimitChange={(e) => handlePagination(e, 'pageSize')}
          handlePageNumberChange={(e) => handlePagination(e, 'pageNumber')}
          handlePageChangeLeft={(e) => handlePagination(e, 'pageNumber')}
          handlePageChangeRight={(e) => handlePagination(e, 'pageNumber')}
          refreshGridClick={refreshGrid}
        />
      </PoDetailsWrapper>
    </Modal>
  );
};

export default AddToDraftWoModal;
